// 参照専用の埋め込みモード用の認証 Shell。
//
// 通常の MeidenConnectAuthShell と異なり、IdP へのリダイレクトも分離ストレージ読取も一切行わない。
// 親フレーム（Connect Note）から postMessage で渡されるトークン（embed-auth.ts）を待ち、
// 受領できたら子（キャンバス）を描画する。受領できない/失効して再取得もできない場合は、
// iframe 内でログイン画面へ遷移させず、「認証に失敗したため表示できませんでした」という静的な
// メッセージを表示する（利用者要件）。
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { AuthSessionContext, type AuthSessionContextValue } from "./auth-session-context";
import { EmbedApiRequestProvider } from "./api-request-context";
import { EmbedCurrentActorProvider } from "./use-current-actor";
import { getEmbedAccessToken, startEmbedAuthListener, subscribeEmbedToken } from "./embed-auth";

/** 親からのトークン受領をこの時間まで待ち、来なければ「認証失敗」表示へ切り替える。 */
const EMBED_TOKEN_WAIT_TIMEOUT_MS = 10_000;

type EmbedAuthPhase = "waiting" | "ready" | "missing";

export function EmbedAuthShell({ children }: { readonly children: ReactNode }): ReactNode {
  const [phase, setPhase] = useState<EmbedAuthPhase>(() =>
    getEmbedAccessToken() !== undefined ? "ready" : "waiting"
  );

  useEffect(() => {
    const stopListener = startEmbedAuthListener();
    const markReadyIfTokenPresent = (): void => {
      if (getEmbedAccessToken() !== undefined) {
        setPhase("ready");
      }
    };
    // 設置直後に既にトークンがある可能性を拾う。
    markReadyIfTokenPresent();
    const unsubscribe = subscribeEmbedToken(markReadyIfTokenPresent);
    // 一定時間トークンが来なければ「認証失敗」表示へ。ただしリスナーは張り続けるので、
    // 後からトークンが届けば ready へ復帰する。
    const timeoutId = window.setTimeout(() => {
      setPhase((current) =>
        current === "ready" ? current : getEmbedAccessToken() !== undefined ? "ready" : "missing"
      );
    }, EMBED_TOKEN_WAIT_TIMEOUT_MS);
    return () => {
      unsubscribe();
      stopListener();
      window.clearTimeout(timeoutId);
    };
  }, []);

  const contextValue = useMemo<AuthSessionContextValue>(
    () => ({
      // ready のときだけ合成の SessionSnapshot を渡す（埋め込みでは氏名・権限は未使用）。
      user: phase === "ready" ? { userId: "", name: "", source: "meiden-connect" } : undefined,
      isRestoring: phase === "waiting",
      signOut: async () => {
        // 埋め込みではサインアウト導線を持たない。
      },
    }),
    [phase]
  );

  if (phase === "waiting") {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <div className="app-bootstrap" aria-busy="true">
          <p>キャンバスを読み込み中…</p>
        </div>
      </AuthSessionContext.Provider>
    );
  }

  if (phase === "missing") {
    return (
      <AuthSessionContext.Provider value={contextValue}>
        <EmbedAuthFailedPane />
      </AuthSessionContext.Provider>
    );
  }

  return (
    <AuthSessionContext.Provider value={contextValue}>
      <EmbedCurrentActorProvider>
        <EmbedApiRequestProvider>{children}</EmbedApiRequestProvider>
      </EmbedCurrentActorProvider>
    </AuthSessionContext.Provider>
  );
}

/**
 * 認証失敗表示。iframe 内でログイン画面へは絶対に遷移させず、静的メッセージのみを表示する。
 * 「再読み込み」は同じ埋め込み URL の再取得で、親から改めてトークンを受け取れれば表示に復帰する。
 */
function EmbedAuthFailedPane(): ReactNode {
  const handleReload = useCallback((): void => {
    window.location.reload();
  }, []);
  return (
    <div className="app-bootstrap" role="alert" aria-live="polite">
      <div>
        <p>認証に失敗したため、キャンバスを表示できませんでした。</p>
        <p>お手数ですが、Connect Note の記事画面から開き直してください。</p>
        <p>
          <button type="button" onClick={handleReload}>
            再読み込みする
          </button>
        </p>
      </div>
    </div>
  );
}
